/**
 * Base Element Schemas
 * 
 * Defines standard event schemas that all elements should implement.
 * These provide a consistent interface for common element lifecycle events.
 */

import type { SchemaDefinition } from '../utils/event-schemas';

/**
 * Standard event schemas that all elements should implement
 */
export const baseElementEvents: Record<string, SchemaDefinition> = {
  'mounted': {
    type: 'object',
    description: 'Fired when the element is mounted in the DOM',
    example: {
      timestamp: new Date().toISOString(),
      elementId: 'element-123',
    },
    fields: {
      timestamp: { 
        type: 'date', 
        required: true, 
        description: 'When the element was mounted' 
      },
      elementId: { 
        type: 'string', 
        required: true, 
        description: 'The unique ID of the element instance' 
      },
    },
  },
  
  'unmounted': {
    type: 'object',
    description: 'Fired when the element is unmounted from the DOM',
    example: {
      timestamp: new Date().toISOString(),
      elementId: 'element-123',
    },
    fields: {
      timestamp: { 
        type: 'date', 
        required: true, 
        description: 'When the element was unmounted' 
      },
      elementId: { 
        type: 'string', 
        required: true, 
        description: 'The unique ID of the element instance' 
      },
    },
  },
  
  'error': {
    type: 'object',
    description: 'Fired when the element encounters an error',
    example: {
      errorMessage: 'Failed to load resource',
      errorCode: 'RESOURCE_LOAD_FAILED',
      timestamp: new Date().toISOString(),
      elementId: 'element-123',
    },
    fields: {
      errorMessage: { 
        type: 'string', 
        required: true, 
        description: 'Error message' 
      },
      errorCode: { 
        type: 'string', 
        required: false, 
        description: 'Error code if available' 
      },
      timestamp: { 
        type: 'date', 
        required: true, 
        description: 'When the error occurred' 
      },
      elementId: { 
        type: 'string', 
        required: true, 
        description: 'The unique ID of the element instance' 
      },
    },
  },
  
  'ready': {
    type: 'object',
    description: 'Fired when the element is fully initialized and ready for interaction',
    example: {
      timestamp: new Date().toISOString(),
      elementId: 'element-123',
    },
    fields: {
      timestamp: { 
        type: 'date', 
        required: true, 
        description: 'When the element became ready' 
      },
      elementId: { 
        type: 'string', 
        required: true, 
        description: 'The unique ID of the element instance' 
      },
    },
  },
  
  'stateChanged': {
    type: 'object',
    description: 'Fired when the element\'s internal state changes',
    example: {
      previousState: 'idle',
      currentState: 'loading',
      timestamp: new Date().toISOString(),
      elementId: 'element-123',
    },
    fields: {
      previousState: { 
        type: 'string', 
        required: false, 
        description: 'Previous state of the element' 
      },
      currentState: { 
        type: 'string', 
        required: true, 
        description: 'Current state of the element' 
      },
      timestamp: { 
        type: 'date', 
        required: true, 
        description: 'When the state changed' 
      },
      elementId: { 
        type: 'string', 
        required: true, 
        description: 'The unique ID of the element instance' 
      },
    },
  }
};

/**
 * Helper function to merge base element events with custom element events
 * 
 * @param customEvents - Custom event schemas specific to an element
 * @returns Combined event schemas with base events and custom events
 */
export function mergeWithBaseEvents(
  customEvents: Record<string, SchemaDefinition>
): Record<string, SchemaDefinition> {
  return {
    ...baseElementEvents,
    ...customEvents
  };
}

/**
 * Element schema interface for standardizing element schema files
 */
export interface ElementSchema {
  ELEMENT_NAME: string;
  ELEMENT_TYPE: string;
  eventSchemas: Record<string, SchemaDefinition>;
  getEventName?: (eventName: string) => string;
}

/**
 * Create a standard getEventName function for an element
 * 
 * @param elementType - The type of the element
 * @returns A function that prefixes event names with the element type
 */
export function createEventNameGetter(elementType: string) {
  return (eventName: string): string => `${elementType}:${eventName}`;
}
